import { useState } from 'react';
import { useAuth } from '../lib/auth.jsx';
import { useData } from '../lib/data.jsx';
import { useUI, Pimg } from '../lib/ui.jsx';
import { rpc } from '../lib/supabase.js';
import { B } from '../lib/util.js';
import { calcTradein } from '../lib/calc.js';

const RSTAT = {
  cert: <span className="chip c-due">รอตรวจรับรอง</span>,
  ready: <span className="chip c-free">พร้อมจำหน่าย</span>,
  sold: <span className="chip c-out">ขายแล้ว</span>,
  scrapped: <span className="chip c-fix">ตัดทิ้ง</span>,
};

export default function TradeIn() {
  const { tradeinGrades, settings, vatRate } = useAuth();
  const { catalog, resale, refresh } = useData();
  const { toast } = useUI();

  const [sku, setSku] = useState('');
  const [grade, setGrade] = useState('B');
  const [hasHistory, setHasHistory] = useState(true);
  const [newBill, setNewBill] = useState(35000);
  const [saleTxn, setSaleTxn] = useState('');
  const [busy, setBusy] = useState(false);

  const cat = catalog.find(c => c.sku === (sku || catalog[0]?.sku));
  const tradeCap = Number(settings.trade_cap_pct ?? 0.40);
  const calc = cat && tradeinGrades.length
    ? calcTradein({ cat, grade, hasHistory, newBill: +newBill, grades: tradeinGrades, tradeCapPct: tradeCap })
    : null;

  // margin สุทธิหลัง VAT — ไทยไม่มี margin scheme มือสอง: ขายต่อต้องนำส่ง VAT จากราคาขายเต็ม
  const netMargin = calc ? Math.round(calc.resale / (1 + vatRate)) - calc.applied : 0;

  const submit = async () => {
    if (!cat || !calc) return;
    if (!saleTxn.trim()) { toast('รับเทิร์นได้เฉพาะตอนซื้อของใหม่ — ต้องใส่เลขบิลขายก่อน'); return; }
    setBusy(true);
    try {
      const res = await rpc('rp_tradein_accept', {
        p: { sku: cat.sku, grade, has_history: hasHistory, new_bill_amount: +newBill, linked_sale_txn: saleTxn.trim() },
      });
      toast(`รับเทิร์น ${cat.name} — ออกเครดิตร้าน ${B(res.applied)} ฿ (เกรด ${res.effective_grade})`);
      setSaleTxn('');
      await refresh();
    } catch (e) { toast(e.message); }
    setBusy(false);
  };

  return (<>
    <div className="grid" style={{ gridTemplateColumns: '1.55fr 1fr', alignItems: 'start' }}>
      <div>
        <h2>ประเมิน Trade-in</h2>
        <div className="hint">รับเทิร์นเมื่อซื้อสินค้าใหม่เท่านั้น · จ่ายเป็นเครดิตร้าน ไม่ใช่เงินสด · ใช้ได้ไม่เกิน {Math.round(tradeCap * 100)}% ของบิลใหม่</div>

        <label className="fld">1 · ตรวจแหล่งที่มา</label>
        <div className="seg">
          <button className={hasHistory ? 'on' : ''} onClick={() => setHasHistory(true)}>ซื้อจาก Camp Studio</button>
          <button className={!hasHistory ? 'on' : ''} onClick={() => setHasHistory(false)}>ไม่มีประวัติซื้อกับร้าน</button>
        </div>
        {!hasHistory && <div className="note show">สินค้าไม่มีประวัติซื้อกับร้าน: ยืนยันของแท้แล้วรับได้ แต่ปรับเกรดลงหนึ่งขั้น (ไม่มีประวัติการซื้อ/การรับประกัน) และส่งต่อแบบไม่มีตรารับรอง</div>}

        <label className="fld">2 · เลือกสินค้าที่รับเข้า</label>
        <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
          <div className="pthumb" style={{ width: 74, height: 62 }}>{cat && <Pimg cat={cat.category} size={54} image={cat.image_url} />}</div>
          <select style={{ flex: 1 }} value={cat?.sku || ''} onChange={e => setSku(e.target.value)}>
            {catalog.map(c => <option key={c.sku} value={c.sku}>{c.name} · {c.sku} — ป้าย {B(c.retail)}</option>)}
          </select>
        </div>

        <label className="fld">3 · ให้เกรดสภาพ</label>
        <div className="cond">
          {tradeinGrades.map((g, i) => (
            <button key={g.key} className={grade === g.key ? `on c${i === 0 ? 0 : i === 1 ? 1 : 3}` : ''}
              onClick={() => setGrade(g.key)}>เกรด {g.key} · {Math.round(Number(g.in_pct) * 100)}%</button>
          ))}
        </div>
        {calc && <div className="hint" style={{ marginTop: 8 }}>
          {calc.g.descr}{!hasHistory && calc.effKey !== grade ? ' · (ปรับเกรดลงเพราะไม่มีประวัติซื้อกับร้าน)' : ''}
        </div>}

        <label className="fld">4 · ยอดบิลของใหม่ที่ลูกค้าจะซื้อ</label>
        <input type="number" min={0} step={500} style={{ width: 130 }} value={newBill} onChange={e => setNewBill(e.target.value)} />
        <div className="hint" style={{ marginTop: 6 }}>trade-in ใช้ได้ไม่เกิน {Math.round(tradeCap * 100)}% ของบิลใหม่ — ที่เหลือจ่ายเงินสดเสมอ</div>

        <label className="fld">5 · เลขบิลขายของใหม่ (บังคับ)</label>
        <input value={saleTxn} onChange={e => setSaleTxn(e.target.value)} placeholder="เลขบิลจาก POS/SaleCore"
          style={{ width: 260, border: 0, borderBottom: '1px solid var(--line2)', background: 'transparent', padding: '8px 2px', fontFamily: 'var(--mono)', fontSize: 14 }} />
      </div>

      <div className="card calc">
        <h2>สรุปดีล</h2>
        {calc && (<>
          <div className="row"><span>เครดิตตามเกรด {calc.effKey}</span><span className="a">{B(calc.credit)}</span></div>
          <div className="row"><span>เพดาน {Math.round(tradeCap * 100)}% ของบิล</span><span className="a">{B(calc.cap)}</span></div>
          <div className="row"><span>เครดิตที่ใช้ได้</span><span className="a" style={{ color: 'var(--gold)' }}>{B(calc.applied)}</span></div>
          <div className="row"><span>ลูกค้าจ่ายเงินสด</span><span className="a">{B(calc.cash)}</span></div>
          <div className="row total"><span>margin สุทธิหลัง VAT</span>
            <span className="a" style={{ color: netMargin > 0 ? 'var(--gold)' : 'var(--ink)', fontWeight: 800 }}>{B(netMargin)} ฿</span></div>
          <div className="hint" style={{ marginTop: 4 }}>ตั้งขาย ~{B(calc.resale)} (รวม VAT) — ไทยไม่มี margin scheme สินค้ามือสอง ขายต่อต้องนำส่ง VAT จากราคาเต็ม margin จริงจึงต่ำกว่าราคาส่วนต่างดิบ ({B(calc.margin)})</div>
          {calc.capped && <div className="note show">เครดิตเกินเพดาน — ใช้ได้สูงสุด {B(calc.cap)} ส่วนที่เหลือของบิลชำระตามปกติ</div>}
          <div className="hint" style={{ marginTop: 8 }}>ราคาจำหน่ายสภาพดี ~{B(calc.resale)} · ตรารับรอง {hasHistory ? 'มี' : 'ไม่มี'}</div>
          <div className="hint">VAT ตอนขายของใหม่คิดจากราคาเต็ม — trade-in เป็นการชำระด้วยสิ่งของ ไม่ใช่ส่วนลด</div>
        </>)}
        <div style={{ marginTop: 14 }}>
          <button className="btn" disabled={busy || !navigator.onLine} onClick={submit}>รับเทิร์น + ออกเครดิตร้าน</button>
        </div>
        <div className="hint" style={{ marginTop: 10 }}>รับเทิร์นแล้วสินค้าเข้าคลังสินค้าสภาพดี รอตรวจรับรองและตั้งราคาจำหน่าย</div>
      </div>
    </div>

    <div className="card" style={{ marginTop: 18 }}>
      <h2>คลังสินค้าสภาพดี</h2>
      <div className="hint">สินค้ารับเทิร์นและอุปกรณ์เช่าที่ครบรอบการใช้งาน รวมอยู่ที่นี่</div>
      <table>
        <thead><tr><th>สินค้า</th><th>ที่มา</th><th>เกรด</th><th className="r">รับเข้า</th><th className="r">ตั้งขาย</th><th className="r">ส่วนต่าง</th><th>สถานะ</th></tr></thead>
        <tbody>
          {resale.map(r => {
            const c = catalog.find(x => x.sku === r.sku);
            return (
              <tr key={r.id}>
                <td><div style={{ display: 'flex', alignItems: 'center', gap: 9 }}>
                  <div className="pthumb" style={{ width: 40, height: 34 }}>{c && <Pimg cat={c.category} size={28} image={c.image_url} />}</div>{r.name}</div></td>
                <td>{r.source === 'tradein' ? <span className="chip c-out">รับเทิร์น</span> : <span className="chip c-fix">ครบรอบเช่า</span>}</td>
                <td className="mono">{r.grade || '—'}</td>
                <td className="r mono">{Number(r.cost_in) ? B(r.cost_in) : '—'}</td>
                <td className="r mono">{B(r.price_out)}</td>
                <td className="r mono" style={{ color: 'var(--gold)', fontWeight: 700 }}>{B(r.price_out - r.cost_in)}</td>
                <td>{RSTAT[r.status]}</td>
              </tr>
            );
          })}
          {!resale.length && <tr><td colSpan={7} className="empty">ยังไม่มีสินค้าสภาพดี</td></tr>}
        </tbody>
      </table>
    </div>
  </>);
}
